import { forEach } from "lodash";
import {
    LeagueService,
    TeamService,
    UserService,
    Ranking,
    RankingStats,
    IRankingEntry,
    TeamMember,
    ILeagueQuery,
    LeagueQuery,
    IPager,
    Contestant,
    IUser,
} from "ng-league";
import { Store } from "@ngrx/store";
import {
    RootState,
    getContestants,
    getLeague,
    entryAvailable,
    LoadContestantsOnceAction,
    getPlayer,
    getTeam,
    LoadTeamOnceAction,
} from "@play/state";
import { IRankingScope } from "./RankingScope";
import { isRosterEnabled, isTeam } from "@play/utility/league";
import { League, Team, Player } from "@play/api-typings";
import { filter, withLatestFrom, switchMap, map } from "rxjs/operators";
import { of } from "rxjs";

export class RankingController {
    public leagueId: number;
    public leagueRaw: League;
    public loading: boolean;
    public user: IUser;
    public contestant: Player | Team;
    public teamMembers: any = {};
    public usersShortDescriptions: any = {};

    public static $inject = ["$scope", "SettingsService", "LeagueService", "TeamService", "UserService", "store"];
    constructor(
        protected $scope: IRankingScope,
        protected SettingsService,
        protected LeagueService: LeagueService,
        protected TeamService: TeamService,
        protected UserService: UserService,
        protected store: Store<RootState>
    ) {
        this.loading = true;
        this.user = this.SettingsService.user;
        this.leagueId = parseInt(this.SettingsService.leagueId, 10);

        // for pagination
        this.$scope.leagueId = this.leagueId;

        this.$scope.pagination = {
            limit: 100,
        };

        this.$scope.getPaginationData = (offset: number, limit: number) => {
            return this.getPaginationData(offset, limit) as any;
        };

        this.$scope.getContestantPaginationData = (contestantId: number, limit: number) => {
            return this.getContestantPaginationData(contestantId, limit) as any;
        };

        const userId = this.SettingsService.user ? +this.SettingsService.user.id : null;

        if (userId) {
            this.store.dispatch(new LoadContestantsOnceAction(this.leagueId, userId));

            const league$ = this.store.select(getLeague(+this.leagueId)).pipe(filter(entryAvailable));

            this.store
                .select(getContestants(this.leagueId, userId))
                .pipe(
                    filter(entryAvailable),
                    map(contestant => (contestant.entry.length ? contestant.entry[0] : null)),
                    withLatestFrom(league$),
                    switchMap(([contestant, league]) => {
                        if (!contestant) {
                            return of(null);
                        }

                        if (isTeam(league.entry)) {
                            this.store.dispatch(new LoadTeamOnceAction(+contestant.id));

                            return this.store.select(getTeam(+contestant.id));
                        }

                        return this.store.select(getPlayer(+contestant.id));
                    })
                )
                .subscribe(contestant => {
                    if (contestant) {
                        this.contestant = contestant;
                    }
                });
        }

        this.store
            .select(getLeague(this.leagueId))
            .pipe(filter(entryAvailable))
            .subscribe(league => (this.leagueRaw = league.entry));
    }

    public getTotalPlayed(stats: RankingStats) {
        return stats.getWins() + stats.getDraws() + stats.getLosses();
    }

    public getWinPercentage(stats: RankingStats) {
        let total = this.getTotalPlayed(stats);

        if (total === 0) {
            return null;
        }

        return Math.round((stats.getWins() / total) * 100);
    }

    public isSelf(contestant: Contestant) {
        return contestant && this.contestant ? +contestant.getId() === +this.contestant.id : false;
    }

    public getContestantUrl(rank: IRankingEntry) {
        if (!rank.isTeam()) {
            return this.getPlayerUrl(rank.getUser());
        }

        if (this.leagueRaw && isRosterEnabled(this.leagueRaw)) {
            return `${this.leagueRaw.uri}roster/${rank.getTeam().getId()}`;
        }

        return `/team/${rank.getTeam().getId()}`;
    }

    public getPlayerUrl(player: IUser) {
        return `/player/${player.getAlias()}`;
    }

    protected shouldLoadTeamMembers(rankings: IRankingEntry[]): boolean {
        return rankings.length > 0 && rankings[0].isTeam();
    }

    protected setTeamMembers(rankings: IRankingEntry[]): void {
        this.teamMembers = {};

        if (this.shouldLoadTeamMembers(rankings)) {
            let leaders = rankings.filter((rank: IRankingEntry) => {
                return rank.getPosition() === 1;
            });

            forEach(leaders, (rank: IRankingEntry) => {
                let team = rank.getTeam();
                this.TeamService.getTeamMembers(team.getId()).then((teamMembers: TeamMember[]) => {
                    this.teamMembers[team.getId()] = teamMembers;
                });
            });
        }
    }

    protected shouldLoadUsersShortDescriptions(rankings: IRankingEntry[]) {
        return rankings.length > 0 && !rankings[0].isTeam();
    }

    protected setUsersShortDescriptions(rankings: IRankingEntry[]) {
        this.usersShortDescriptions = {};

        if (this.shouldLoadUsersShortDescriptions(rankings)) {
            let leaders = rankings.filter((rank: IRankingEntry) => {
                return rank.getPosition() === 1;
            });

            forEach(leaders, (rank: IRankingEntry) => {
                let user = rank.getUser();
                this.UserService.getShortDescription(user.getId()).then((shortDescription: string) => {
                    this.usersShortDescriptions[user.getId()] = shortDescription;
                });
            });
        }
    }

    protected loadData(ranking: IRankingEntry[]) {
        this.setTeamMembers(ranking);
        this.setUsersShortDescriptions(ranking);
    }

    protected setTiebreakerTypes(types: string[]) {
        this.$scope.tiebreakerTypes = types;
    }

    protected setRankingData(data: Ranking): any {
        let pager: IPager = data.getPager();
        let ranking = data.getRanking();
        this.loadData(ranking);
        this.setTiebreakerTypes(data.getTiebreakerTypes());

        return {
            pager: pager,
            data: ranking,
        };
    }

    protected getContestantPaginationData(contestantId: number, limit: number) {
        let query: ILeagueQuery = {
            id: this.leagueId,
            contestantId: contestantId,
            limit: {
                total: limit,
            },
        };

        return this.LeagueService.getRanking(query as LeagueQuery).then(this.setRankingData.bind(this));
    }

    protected getPaginationData(offset: number, limit: number) {
        let query: ILeagueQuery = {
            id: this.leagueId,
            limit: {
                offset: offset,
                total: limit,
            },
        };

        return this.LeagueService.getRanking(query as LeagueQuery).then(this.setRankingData.bind(this));
    }
}
